<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useDealStagesStore } from 'dashboard/stores/dealStages';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const dealStagesStore = useDealStagesStore();

const dialogRef = ref(null);
const deleteConfirmDialogRef = ref(null);
const isAdding = ref(false);
const showAddErrors = ref(false);
const stagePendingDelete = ref(null);

const addForm = reactive({
  name: '',
  stageType: 'open',
});

const stages = computed(() => dealStagesStore.orderedStages);

const stageTypeOptions = computed(() => [
  { value: 'open', label: t('CRM_FUNIL.STAGE_CONFIG.TYPE.OPEN') },
  { value: 'won', label: t('CRM_FUNIL.STAGE_CONFIG.TYPE.WON') },
  { value: 'lost', label: t('CRM_FUNIL.STAGE_CONFIG.TYPE.LOST') },
]);

const isAddNameInvalid = computed(
  () => showAddErrors.value && !addForm.name.trim()
);

// The generic CRUD factory's `create`/`update` actions push the raw API
// response (snake_case) into the store, while `fetch` camelizes it — so a
// stage created in this session can carry `stage_type` instead of
// `stageType` until the next full fetch. Reading both keeps the badge
// correct either way without touching the shared store/CRUD factory.
const stageTypeOf = stage => stage.stageType || stage.stage_type;

const stageTypeLabel = stage => {
  const type = stageTypeOf(stage);
  const labels = {
    open: t('CRM_FUNIL.STAGE_CONFIG.TYPE.OPEN'),
    won: t('CRM_FUNIL.STAGE_CONFIG.TYPE.WON'),
    lost: t('CRM_FUNIL.STAGE_CONFIG.TYPE.LOST'),
  };
  return labels[type] || type;
};

const resetAddForm = () => {
  addForm.name = '';
  addForm.stageType = 'open';
  showAddErrors.value = false;
};

const open = () => {
  resetAddForm();
  dialogRef.value?.open();
};

const handleAdd = async () => {
  showAddErrors.value = true;
  if (!addForm.name.trim()) return;

  isAdding.value = true;
  try {
    await dealStagesStore.create({
      name: addForm.name.trim(),
      stage_type: addForm.stageType,
    });
    resetAddForm();
  } catch {
    useAlert(t('CRM_FUNIL.STAGE_CONFIG.ERROR'));
  } finally {
    isAdding.value = false;
  }
};

const handleRename = async (stage, value) => {
  const name = (value || '').trim();
  if (!name || name === stage.name) return;

  try {
    await dealStagesStore.update({ id: stage.id, name });
    useAlert(t('CRM_FUNIL.STAGE_CONFIG.SAVED'));
  } catch {
    useAlert(t('CRM_FUNIL.STAGE_CONFIG.ERROR'));
  }
};

const reorderTo = async orderedIds => {
  try {
    await dealStagesStore.reorder(orderedIds);
  } catch {
    useAlert(t('CRM_FUNIL.STAGE_CONFIG.ERROR'));
  }
};

const moveUp = stage => {
  const list = stages.value;
  const index = list.findIndex(s => s.id === stage.id);
  if (index <= 0) return;

  const ids = list.map(s => s.id);
  [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
  reorderTo(ids);
};

const moveDown = stage => {
  const list = stages.value;
  const index = list.findIndex(s => s.id === stage.id);
  if (index === -1 || index >= list.length - 1) return;

  const ids = list.map(s => s.id);
  [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
  reorderTo(ids);
};

// Deleting is destructive and irreversible (Fase 5 shipped it without a
// confirmation step). `handleDelete` only opens the confirmation dialog;
// the actual `dealStagesStore.delete` call happens in `confirmDelete`.
const handleDelete = stage => {
  stagePendingDelete.value = stage;
  deleteConfirmDialogRef.value?.open?.();
};

const confirmDelete = async () => {
  const stage = stagePendingDelete.value;
  deleteConfirmDialogRef.value?.close?.();
  if (!stage) return;

  try {
    await dealStagesStore.delete(stage.id);
    useAlert(t('CRM_FUNIL.STAGE_CONFIG.DELETE.SUCCESS'));
  } catch (error) {
    if (error?.message === 'stage_has_deals') {
      useAlert(t('CRM_FUNIL.STAGE_CONFIG.DELETE.BLOCKED'));
    } else {
      useAlert(t('CRM_FUNIL.STAGE_CONFIG.ERROR'));
    }
  } finally {
    stagePendingDelete.value = null;
  }
};

defineExpose({
  open,
  addForm,
  stages,
  handleAdd,
  handleRename,
  handleDelete,
  confirmDelete,
  moveUp,
  moveDown,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CRM_FUNIL.STAGE_CONFIG.TITLE')"
    width="lg"
    overflow-y-auto
    :show-confirm-button="false"
    @confirm="handleAdd"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <ul class="flex flex-col gap-2">
        <li
          v-for="(stage, index) in stages"
          :key="stage.id"
          class="flex items-center gap-2 p-2 border rounded-lg border-n-weak"
        >
          <div class="flex flex-col">
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-chevron-up"
              :disabled="index === 0"
              :aria-label="t('CRM_FUNIL.STAGE_CONFIG.MOVE_UP')"
              @click="moveUp(stage)"
            />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-chevron-down"
              :disabled="index === stages.length - 1"
              :aria-label="t('CRM_FUNIL.STAGE_CONFIG.MOVE_DOWN')"
              @click="moveDown(stage)"
            />
          </div>

          <InlineInput
            :id="`stage-name-${stage.id}`"
            class="flex-1"
            :model-value="stage.name"
            @blur="value => handleRename(stage, value)"
          />

          <span
            class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap bg-n-slate-3 text-n-slate-11"
          >
            {{ stageTypeLabel(stage) }}
          </span>

          <Button
            variant="faded"
            color="ruby"
            size="xs"
            icon="i-lucide-trash"
            @click="handleDelete(stage)"
          />
        </li>
      </ul>

      <div class="flex items-end gap-2 pt-3 border-t border-n-weak">
        <Input
          v-model="addForm.name"
          class="flex-1"
          :placeholder="t('CRM_FUNIL.STAGE_CONFIG.ADD.NAME_PLACEHOLDER')"
          :message="
            isAddNameInvalid
              ? t('CRM_FUNIL.STAGE_CONFIG.ADD.NAME_REQUIRED')
              : ''
          "
          :message-type="isAddNameInvalid ? 'error' : 'info'"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.STAGE_CONFIG.ADD.TYPE') }}
          </label>
          <Select v-model="addForm.stageType" :options="stageTypeOptions" />
        </div>
        <Button
          type="submit"
          :label="t('CRM_FUNIL.STAGE_CONFIG.ADD.SUBMIT')"
          :is-loading="isAdding"
          :disabled="isAdding"
        />
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="deleteConfirmDialogRef"
    type="alert"
    :title="t('CRM_FUNIL.STAGE_CONFIG.DELETE.CONFIRM')"
    @confirm="confirmDelete"
  />
</template>
